// @ts-check
"use strict";

import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import * as Sentry from "@sentry/react";
import { useQueryClient } from "@tanstack/react-query";
import { loadGuestChatFromStorage, clearGuestChatStorage } from "./guest-chat-provider";
import { useCreateChatSession, useUpdateChatSession } from "./sessions";
import { ChatSessionQueryKeys } from "./sessions/query";
import { ChatImagesQueryKeys } from "./images/query";
import { ChatMessageQueryKeys } from "./messages/query";
import axios from "axios";

/**
 * Hook to restore guest chat messages after login
 * @param {Object} params
 * @param {ReturnType<typeof useCreateChatSession>["mutateAsync"]} params.createNewChatSession - Function to create a new chat session (uses existing empty session if available)
 * @param {ReturnType<typeof useUpdateChatSession>["mutateAsync"]} params.updateSession - Function to update a session
 * @param {boolean} params.isInitialized - Whether the chat system is initialized
 */
export default function useGuestChatRestoration({
  createNewChatSession,
  updateSession,
  isInitialized,
}) {
  // @ts-ignore
  const { user } = useSelector((state) => state.app.datastate);
  const queryClient = useQueryClient();
  const restorationAttemptedForUserRef = useRef(null);
  const isRestoringRef = useRef(false);

  useEffect(() => {
    // CRITICAL: Wait for initialization to complete before restoring
    // This ensures the session list has been fetched from the backend
    // Otherwise our restored session gets wiped out by the initial fetch
    if (!isInitialized) return;
    if (!user?._id) return;

    // Check if we've already restored for this user
    if (restorationAttemptedForUserRef.current === user._id) return;
    if (isRestoringRef.current) return;

    const guestMessages = loadGuestChatFromStorage();
    if (!guestMessages || guestMessages.length === 0) return;

    // Mark this user as attempted
    restorationAttemptedForUserRef.current = user._id;
    isRestoringRef.current = true;

    restoreGuestChat(guestMessages)
      .catch((err) => {
        console.error("Guest chat restoration failed:", err);
        Sentry.captureException(err, {
          extra: {
            context: "useGuestChatRestoration - restore failed",
            messageCount: guestMessages?.length,
            userId: user?._id,
          },
        });
        // Reset so user can retry on next login
        restorationAttemptedForUserRef.current = null;
      })
      .finally(() => {
        isRestoringRef.current = false;
      });
  }, [user?._id, isInitialized]); // Wait for both user login AND initialization!

  /**
   * Main restoration function
   * @param {Array<ChatMessage>} guestMessages
   */
  async function restoreGuestChat(guestMessages) {
    try {
      // Step 1: Use createNewChatSession instead of manually creating a session
      // This ensures it follows the same flow as normal session creation
      // and reuses any existing empty session
      const { session: newSession } = await createNewChatSession();

      if (!newSession || !newSession._id) {
        throw new Error("Failed to create session for guest chat restoration");
      }

      const sessionId = newSession._id;

      // Step 2: Convert guest messages to backend format and collect images
      const { messages: convertedMessages, images: chatImages } = await convertGuestMessages(
        guestMessages,
        user,
        sessionId
      );

  

      await updateSession({
        sessionId,
        sessionInfo: {
          chat: convertedMessages,
          title: convertedMessages[0]?.text?.slice(0, 50) || "Restored Chat",
          images: chatImages.map(img => img._id),
        },
      });

      // Invalidate cached session + messages + images so the UI reflects the restored
      // data without a refresh. Messages must be invalidated too: createNewChatSession
      // pre-seeds an empty messages page for the new session (to avoid a loading-skeleton
      // flash), and that page has staleTime: Infinity, so without this it never refetches
      // and the restored chat appears to have a title but no messages.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ChatSessionQueryKeys.CHAT_SESSION(sessionId) }),
        queryClient.invalidateQueries({ queryKey: ChatMessageQueryKeys.CHAT_MESSAGES(sessionId) }),
        queryClient.invalidateQueries({ queryKey: ChatImagesQueryKeys.CHAT_SESSION_IMAGES(sessionId) }),
      ]);

      clearGuestChatStorage();
    } catch (err) {
      throw new Error(`Failed to restore guest chat: ${err.message}`);
    }
  }

  /**
   * Convert guest message format to backend format
   * @param {Array<ChatMessage>} guestMessages
   * @param {Object} user
   * @param {string} sessionId
   * @returns {Promise<{messages: Array<ChatMessage>, images: Array<ChatImage>}>}
   */
  async function convertGuestMessages(guestMessages, user, sessionId) {
    const converted = [];
    const images = [];

    for (const msg of guestMessages) {
      if (msg.user === "user") {
        // User message
        const userMsg = {
          user: user._id,
          text: msg.text || msg.content,
          timestamp: msg.timestamp,
        };

        // Handle images (DataURL → backend Image doc → ChatImage with _id)
        // Support both new format (msg.images[]) and old format (msg.image)
        const imageDataUrls = msg.images?.length ? msg.images : msg.image ? [msg.image] : [];
        /** @type {string[]} */
        const uploadedImageIds = [];
        for (const dataUrl of imageDataUrls) {
          try {
            /** @type {ChatImage} */
            const chatImage = await uploadDataURLImage(dataUrl);
            images.push(chatImage);
            if (chatImage._id) uploadedImageIds.push(chatImage._id);
          } catch (err) {
            console.error("Failed to upload image, skipping:", err);
            Sentry.captureException(err, {
              extra: { context: "convertGuestMessages - image upload", userId: user._id },
            });
          }
        }
        if (uploadedImageIds.length > 0) {
          // @ts-ignore
          userMsg.images = uploadedImageIds;
        }

        // Handle files (text content)
        // Support both new format (msg.files[]) and old format (msg.file)
        const fileContents = msg.files?.length ? msg.files : msg.file ? [msg.file] : [];
        if (fileContents.length > 0) {
          userMsg.files = fileContents.map(content => ({ content }));
        }

        converted.push(userMsg);
      } else if (msg.user === "AI" || msg.user === "agent") {
        // AI/Agent message
        let text = msg.text || msg.chunks?.join("") || extractTextFromMessageParts(msg.message);

        // Ensure text is a string and handle null/undefined
        text = text || "";

        const aiMsg = {
          // Preserve the original user type ('AI' or 'agent')
          // This is critical for proper rendering - agent messages need to be rendered as AgentChatBubble
          user: msg.user === "agent" ? "agent" : "AI",
          text: text,
          timestamp: msg.timestamp,
        };

        // For agent mode, preserve message structure (web search, etc.)
        // This contains citations, tool calls, and image search results
        if (msg.message && msg.message.length > 0) {
          aiMsg.message = msg.message;
        }

        // Only add if there's actual content
        if (text || aiMsg.message) {
          converted.push(aiMsg);
        }
      }
    }
    return { messages: converted, images };
  }

  /**
   * Convert DataURL to a backend Image document via POST /upload/images
   * @param {string} dataURL
   * @returns {Promise<ChatImage>}
   */
  async function uploadDataURLImage(dataURL) {
    const blob = dataURItoBlob(dataURL);
    const file = new File([blob], `restored-image-${Date.now()}.png`, { type: blob.type });
    const formData = new FormData();
    formData.append("file", file);
    const uploadEndpoint = new URL("upload/images", import.meta.env.VITE_SOCKET_URL).href;
    const { data, status } = await axios.post(uploadEndpoint, formData);
    if (status !== 200) throw new Error("Error uploading image");
    return /** @type {ChatImage} */ (data.data.image);
  }

  /**
   * Extract text from agent message parts
   * @param {Array<ChatMessagePart>} messageParts
   * @returns {string}
   */
  function extractTextFromMessageParts(messageParts) {
    if (!messageParts) return "";
    return messageParts
      .filter((part) => part.event === "RunResponseContent")
      .map((part) => part.data?.content || "")
      .join("");
  }

  /**
   * Helper to convert DataURL to Blob
   * @param {string} dataURL
   * @returns {Blob}
   */
  function dataURItoBlob(dataURL) {
    const parts = dataURL.split(",");
    const mime = parts[0].match(/:(.*?);/)[1];
    const bstr = atob(parts[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }
    return new Blob([u8arr], { type: mime });
  }
}
